import { comma } from "@library/Utilities";
import type { IVehicle } from "../IVehicle";
import type { IConditionDto } from "./ICondition";
import type { IPosition } from "./IPosition";

export class OdometerPosition implements IPosition
{

    next: number;
    last: number;
    current: number;
    score: number = 0;
    label: string = '';
    relativeLabel: string = '';
    isIncomplete: boolean = false;
    isOverdue: boolean = false;
    isDue: boolean = false;

    private usesOdometer: boolean;

    constructor( vehicle: IVehicle, model: IConditionDto, currentOdometer: number | undefined, usesOdometer: boolean )
    {
        this.usesOdometer = usesOdometer;
        this.next = model.nextOdometer ?? 0;
        this.current = currentOdometer ?? 0;
        this.last = this.getLastOdometer( vehicle, model.lastOdometer );
        this.initialise( model.score );
    }
    
    /**
     * Initialises the position once the next, current and last are set
     */
    private initialise( score?: number )
    {
        this.score = score ?? this.getScore();
        this.label = `${ comma( this.next - this.current )}km`;
        this.relativeLabel = this.getRelativeLabel();
        this.isIncomplete = this.usesOdometer == false || ( this.next == 0 || this.current == 0 );
        this.isOverdue = this.usesOdometer && this.next > 0 && this.next <= this.current;
        this.isDue = this.isIncomplete == false && this.isOverdue == false && this.usesOdometer && this.score > 0 && this.score < 0.2;
    }

    /**
     * Sets the parameters of the position
     * @param next 
     * @param current 
     * @param last 
     */
    set( next: number, current?: number, last?: number )
    {
        this.next = next;
        this.current = current ?? this.current;
        this.last = last ?? this.current;
        this.initialise();
    }

    /**
     * If we have a next odometer, but no last odometer, calculate the last value
     * to be the next minus an estimated number of annual kilometers
     * @param lastOdometer 
     * @returns 
     */
    private getLastOdometer( vehicle: IVehicle, lastOdometer: number | undefined )
    {
        const last = lastOdometer ?? -1;
        if( last == -1 )
        {
            return Math.max( this.next - vehicle.estimatedAnnualKilometers, 0 );
        }  
        return last;
    }

    /**
     * Calculates the score for the odometer condition
     * @returns 
     */
    private getScore()
    {

        if( this.usesOdometer == false ) return 1; // this condition doesnt use the odometer
        if( this.next == 0 || this.current == 0 ) return 1; // cant process it
        if( this.next < this.current ) return 0; // overdue
        if( this.last == 0 ) return 1; // cant process it
        if( this.current < this.last ) return 1; // invalid, current cant be less than previous

        const normalisedNext = this.next - this.last; // next from last
        const normalisedCurrent = this.current - this.last; // current from last

        return Math.round( ( 1 - Math.min( Math.max( normalisedCurrent / normalisedNext, 0 ), 1 ) ) * 100 ) / 100;

    }

    /**
     * Gets the label for the odometer condition
     * @returns 
     */
    private getRelativeLabel()
    {

        if( this.current == 0 || this.next == 0 || this.usesOdometer == false ) return '';

        var difference = this.next - this.current;
        
        if( difference == 0 ) // Due now
        {
            return '-1km ago';
        }
        
        else if( difference > 0 ) // Due soon 
        {
            return `In ${ comma( difference ) }km`;
        }

        else if( difference <= 0 ) // Overdue
        {
            return `${ comma( Math.abs( difference ) ) }km ago`;
        }

        return '';

    }

}